/**
 * va-fiche-produit-2026.js · La fiche technique du morceau, dans sa fenêtre.
 * =========================================================================
 *
 * CE QUE ÇA RÈGLE. La fenêtre d'un morceau dit son nom, son prix et une
 * phrase. Le client qui veut savoir ce qu'il achète — la matière, le poids du
 * tissu, comment ça taille — n'a nulle part où regarder. Il écrit un courriel,
 * ou il n'achète pas. Les deux coûtent cher, et le deuxième ne se compte
 * nulle part.
 *
 * Ici : un bouton sous la description, « Fiche technique et grille des
 * tailles ». On clique, la fiche s'ouvre à même la fenêtre. Composition,
 * poids, finition, certification, provenance, et la grille des tailles en
 * pouces ET en centimètres. Rien à télécharger, rien à quitter, pas de
 * deuxième fenêtre qui recouvre la première.
 *
 * OÙ VIT LA DONNÉE. Dans un fichier du thème, `va-fiches-2026.json`, rangé par
 * titre de produit. Pas dans ce code. Ajouter un morceau, corriger un poids,
 * changer une mesure : on touche au fichier de données, jamais au programme.
 * C'est ce qui rend les 35 morceaux tenables.
 *
 * POURQUOI UN MODULE À PART. L'application est compilée et on n'en a pas les
 * sources : une modification dedans saute au prochain build, et une erreur de
 * syntaxe tue la page au complet. Ce module observe le DOM ; si la fenêtre
 * change de forme, le bouton n'apparaît plus, la page continue de marcher.
 *
 * CE QU'IL NE DIT JAMAIS. Le nom du fournisseur, ni un lien vers son site.
 * La provenance, oui — c'est au client. Chez qui on l'achète, non.
 */
(function () {
  "use strict";

  var MARQUE = "vaFicheTech";
  var POUCE = 2.54;
  var donnees = null;
  var chargement = null;

  /**
   * L'adresse du fichier de données. Elle est posée par le thème sur la balise
   * du script (`data-fiches`), avec l'empreinte « ?v=… » que Shopify calcule.
   * C'est ce qui fait qu'une correction de fiche arrive dans les navigateurs
   * au lieu de rester coincée derrière un cache d'un an.
   */
  function url() {
    var s = document.querySelector("script[data-fiches]");
    if (s && s.getAttribute("data-fiches")) return s.getAttribute("data-fiches");
    var f = document.querySelector('script[src*="va-fiche-produit-2026"]');
    var src = f ? f.getAttribute("src") : "";
    return src ? src.replace(/va-fiche-produit-2026\.js/, "va-fiches-2026.json") : "";
  }

  function charger() {
    if (donnees) return Promise.resolve(donnees);
    if (chargement) return chargement;
    var u = url();
    if (!u) return Promise.resolve(null);
    chargement = fetch(u, { credentials: "omit" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { donnees = (j && j.fiches) || null; return donnees; })
      .catch(function () { return null; });
    return chargement;
  }

  function cm(pouces) {
    var v = Math.round(Number(pouces) * POUCE * 2) / 2;
    return String(v).replace(".", ",");
  }

  function po(v) {
    return String(v).replace(".", ",");
  }

  /* ---------------------------------------------------------------- */
  /* Le contenu                                                        */
  /* ---------------------------------------------------------------- */

  function el(tag, cls, txt) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (txt != null) e.textContent = txt;
    return e;
  }

  function bloc(titre) {
    var d = el("div", "mt-3");
    d.appendChild(el("p", "mb-1 text-[10px] font-bold uppercase tracking-wide text-ink/45", titre));
    return d;
  }

  function grille(g) {
    var d = bloc("Grille des tailles");
    d.appendChild(el("p", "mb-1.5 text-[11px] text-ink/50", g.type + ". Pouces, et centimètres entre parenthèses."));

    var enveloppe = el("div", "overflow-x-auto");
    var t = el("table", "w-full border-collapse text-[11px] tabular-nums");

    var thead = el("thead");
    var tr = el("tr");
    tr.appendChild(el("th", "border-b border-ink/10 py-1 pr-2 text-left font-bold text-ink/60", ""));
    g.tailles.forEach(function (x) {
      tr.appendChild(el("th", "border-b border-ink/10 px-1 py-1 text-center font-bold text-ink/70", x));
    });
    thead.appendChild(tr);
    t.appendChild(thead);

    var tb = el("tbody");
    g.lignes.forEach(function (ligne) {
      var r = el("tr");
      var c0 = el("td", "border-b border-ink/5 py-1 pr-2 align-top");
      c0.appendChild(el("span", "block font-semibold text-ink/75", ligne.nom));
      if (ligne.precision) c0.appendChild(el("span", "block text-[10px] text-ink/45", ligne.precision));
      r.appendChild(c0);
      ligne.valeurs.forEach(function (v) {
        var c = el("td", "border-b border-ink/5 px-1 py-1 text-center align-top");
        c.appendChild(el("span", "block whitespace-nowrap text-ink/75", po(v) + '"'));
        c.appendChild(el("span", "block whitespace-nowrap text-[10px] text-ink/40", cm(v) + " cm"));
        r.appendChild(c);
      });
      tb.appendChild(r);
    });
    t.appendChild(tb);
    enveloppe.appendChild(t);
    d.appendChild(enveloppe);
    return d;
  }

  function panneau(f) {
    var p = el("div", "mt-2 max-h-[55vh] overflow-y-auto rounded-xl border border-ink/10 bg-paper/60 p-3");
    p.hidden = true;

    if (Array.isArray(f.composition) && f.composition.length) {
      var d = bloc("Composition");
      f.composition.forEach(function (paire) {
        var l = el("p", "text-[11px] leading-snug text-ink/70");
        l.appendChild(el("span", "font-semibold text-ink/80", paire[0] + " · "));
        l.appendChild(document.createTextNode(paire[1]));
        d.appendChild(l);
      });
      p.appendChild(d);
    }

    if (f.poids) {
      var w = bloc("Poids du tissu");
      w.appendChild(el("p", "text-[11px] text-ink/70", f.poids));
      p.appendChild(w);
    }

    if (Array.isArray(f.details) && f.details.length) {
      var dd = bloc("Finition");
      var ul = el("ul", "list-disc pl-4 text-[11px] leading-snug text-ink/70");
      f.details.forEach(function (x) { ul.appendChild(el("li", "mb-0.5", x)); });
      dd.appendChild(ul);
      p.appendChild(dd);
    }

    if (f.certification || f.origine) {
      var c = bloc("Certification et provenance");
      if (f.certification) c.appendChild(el("p", "text-[11px] text-ink/70", f.certification));
      if (f.origine) c.appendChild(el("p", "text-[11px] text-ink/70", f.origine));
      p.appendChild(c);
    }

    if (f.grille && f.grille.tailles && f.grille.lignes) p.appendChild(grille(f.grille));

    return p;
  }

  /* ---------------------------------------------------------------- */
  /* L'accroche                                                        */
  /* ---------------------------------------------------------------- */

  function accrocher(titreEl, f) {
    var zone = titreEl.parentElement;
    if (!zone || zone.dataset[MARQUE]) return;
    zone.dataset[MARQUE] = "1";

    var p = panneau(f);

    var bouton = el("button", "mt-1.5 inline-flex items-center gap-1 rounded-full border border-ink/15 bg-white px-2.5 py-1 text-[11px] font-bold text-ink/70 transition hover:border-ink/35 hover:text-ink");
    bouton.type = "button";
    var fleche = el("span", "text-[9px] leading-none", "▾");
    bouton.appendChild(el("span", null, "Fiche technique et grille des tailles"));
    bouton.appendChild(fleche);
    bouton.setAttribute("aria-expanded", "false");

    bouton.addEventListener("click", function () {
      p.hidden = !p.hidden;
      fleche.textContent = p.hidden ? "▾" : "▴";
      bouton.setAttribute("aria-expanded", p.hidden ? "false" : "true");
    });

    /* Juste sous la description, avant le prix : c'est là que la question se
       pose, pas au fond de la fenêtre. */
    var desc = zone.querySelector("p");
    if (desc && desc.nextSibling) {
      zone.insertBefore(bouton, desc.nextSibling);
      zone.insertBefore(p, bouton.nextSibling);
    } else {
      zone.appendChild(bouton);
      zone.appendChild(p);
    }
  }

  /**
   * Le titre affiché doit retrouver sa fiche même si l'apostrophe n'est pas la
   * même. « Manteau d’hiver » avec l'apostrophe courbe et « Manteau d'hiver »
   * avec la droite sont deux chaînes différentes pour un ordinateur, et le même
   * manteau pour tout le monde. Ça s'est déjà produit une fois ; on ne laisse
   * pas une fiche disparaître pour un caractère.
   */
  function pareil(a, b) {
    var n = function (s) {
      return String(s || "").replace(/[\u2018\u2019\u02BC]/g, "'")
        .replace(/\s+/g, " ").trim().toLowerCase();
    };
    return n(a) === n(b);
  }

  function fichePour(titre) {
    if (donnees[titre]) return donnees[titre];
    for (var k in donnees) if (pareil(k, titre)) return donnees[k];
    return null;
  }

  function chercher() {
    if (!donnees) return;
    try {
      var h = document.querySelectorAll("h2.font-display, h1.font-display, h3.font-display");
      for (var i = 0; i < h.length; i++) {
        var t = (h[i].textContent || "").trim();
        if (!t) continue;
        var f = fichePour(t);
        if (f) accrocher(h[i], f);
      }
    } catch (_) { /* jamais au détriment de la page */ }
  }

  var minuteur = 0;
  function demarrer() {
    charger().then(function (d) {
      if (!d) return;
      chercher();
      try {
        new MutationObserver(function () {
          if (minuteur) return;
          minuteur = setTimeout(function () { minuteur = 0; chercher(); }, 200);
        }).observe(document.documentElement, { childList: true, subtree: true });
      } catch (_) { /* au mieux */ }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", demarrer);
  } else {
    demarrer();
  }
})();
